import React from "react";

// AI-GENERATED | 365 Copilot (GPT-5) | Tarkistanut Oskari Rintamäki | 2026-04-10
// Renders clickable links from plain URLs and Markdown-style links [text](url)
// while preserving line breaks and avoiding double-linking.
export const renderLinksInText = (text) => {
  if (text == null) return null;
  const str = String(text);

  // --- Regexes ---
  // Markdown links: [text](url)
  const markdownLinkRegex = /\[([^\]]+)\]\(([^)\s]+)\)/g;

  // Plain URLs (http(s) or www.*)
  const urlRegex = /(https?:\/\/[^\s<>\"']+|www\.[^\s<>\"']+)/gi;

  const nodes = [];
  let lastIndex = 0;
  let matchIndex = 0;

  let mdMatch;

  // ---------- 1. Handle Markdown links first ----------
  while ((mdMatch = markdownLinkRegex.exec(str)) !== null) {
    const matchStart = mdMatch.index;
    const matchText = mdMatch[0];
    const linkText = mdMatch[1];
    const rawUrl = mdMatch[2];

    // Push text before markdown link
    if (matchStart > lastIndex) {
      const before = str.slice(lastIndex, matchStart);
      pushTextWithLineBreaks(nodes, before, `mdt-${matchIndex}-${lastIndex}`);
    }

    // Normalize href
    const href = /^https?:\/\//i.test(rawUrl)
      ? rawUrl
      : `http://${rawUrl}`;

    let safeHref;
    try {
      safeHref = encodeURI(href);
    } catch {
      safeHref = href;
    }

    nodes.push(
      <a
        key={`md-${matchIndex}-${lastIndex}`}
        href={safeHref}
        target="_blank"
        rel="noopener noreferrer"
      >
        {linkText}
      </a>
    );

    lastIndex = matchStart + matchText.length;
    matchIndex++;
  }

  // Remaining text after last markdown link
  const remainingText = str.slice(lastIndex);

  // ---------- 2. Handle plain URLs inside remaining text ----------
  if (remainingText) {
    let urlLastIndex = 0;
    let urlMatch;

    while ((urlMatch = urlRegex.exec(remainingText)) !== null) {
      const matchStart = urlMatch.index;
      const matchText = urlMatch[0];

      if (matchStart > urlLastIndex) {
        const before = remainingText.slice(urlLastIndex, matchStart);
        pushTextWithLineBreaks(
          nodes,
          before,
          `t-${matchIndex}-${urlLastIndex}`
        );
      }

      // Trim trailing punctuation
      let urlCoreEnd = matchText.length;
      const trailingPunct = '.,;:!?)]}';
      while (
        urlCoreEnd > 0 &&
        trailingPunct.includes(matchText.charAt(urlCoreEnd - 1))
      ) {
        urlCoreEnd--;
      }

      const core = matchText.slice(0, urlCoreEnd);
      const trail = matchText.slice(urlCoreEnd);

      const href = /^https?:\/\//i.test(core)
        ? core
        : `http://${core}`;

      let safeHref;
      try {
        safeHref = encodeURI(href);
      } catch {
        safeHref = href;
      }

      const decodedUrl = decodeURI(core);

      nodes.push(
        <a
          key={`a-${matchIndex}-${urlLastIndex}`}
          href={safeHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          {decodedUrl}
        </a>
      );

      if (trail) {
        pushTextWithLineBreaks(
          nodes,
          trail,
          `trail-${matchIndex}-${urlLastIndex}`
        );
      }

      urlLastIndex = matchStart + matchText.length;
      matchIndex++;
    }

    if (urlLastIndex < remainingText.length) {
      pushTextWithLineBreaks(
        nodes,
        remainingText.slice(urlLastIndex),
        `t-last-${urlLastIndex}`
      );
    }
  }

  // ---------- 3. Fallback: no links found ----------
  if (nodes.length === 0) {
    return formatTextWithBreaks(str);
  }

  return nodes;
};

// Helper: splits text on \n and inserts <br/> nodes, pushing onto target array
function pushTextWithLineBreaks(targetArray, text, keyPrefix) {
  if (!text) return;
  const parts = text.split('\n');
  parts.forEach((part, idx) => {
    if (part.length > 0) {
      targetArray.push(<span key={`${keyPrefix}-p-${idx}`}>{part}</span>);
    }
    if (idx !== parts.length - 1) {
      targetArray.push(<br key={`${keyPrefix}-br-${idx}`} />);
    }
  });
}

// Helper: returns a React fragment preserving line breaks for a whole text block
function formatTextWithBreaks(text) {
  const parts = String(text).split('\n');
  return parts.map((p, i) => (
    <React.Fragment key={`fb-${i}`}>
      {p}
      {i !== parts.length - 1 && <br />}
    </React.Fragment>
  ));
}
